import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { EventTimeService } from './event.time.service';
import { ApiService } from '../../../common/services/api/api.service';
import { inject, Injectable } from '@angular/core';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { CalendarQueryCommand } from '../../shared/models/calendar.action.command';
import {
  clone,
  isEmptyArray,
  isNullOrUndefined,
  isObject,
  upperFirstLetter,
} from 'src/app/common/utils/object.extensions';
import {
  GooglePlaceReview,
  PlaceAdditionalData,
  VisitorPlace,
} from '../../shared/models/visitor.place';
import { CreatePlaceCommand } from '../../shared/models/create.place.command';
import { PlaceType } from '../../shared/models/place.type';
import { VisitorEvent } from '../../shared/models/visitor.event';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/common/services/config/config.service';
import { ActivatedRoute, Params } from '@angular/router';
import { IAppState, IFrontendSettings } from '../../shared/ngrx.stores/frontend.settings/states';
import * as PlaceQueryCommandActions from '../../place-calendar/ngrx.stores/place.query.command/actions';
import { Store } from '@ngrx/store';
import { Location } from '@angular/common';
import { VisitorService } from '../../shared/services/visitor.service';
import { routeConfigs } from 'src/app/common/constants/route.config';
import { config } from 'src/app/config/config';
import { EditPlaceCommand } from '../../shared/models/edit.place.command';

@Injectable({
  providedIn: 'root',
})
export class VisitorPlaceService {
  constructor(
    public sanitizer: DomSanitizer,
    public visitorService: VisitorService,
    private _apiService: ApiService,
    private _eventTimeService: EventTimeService,
    private _store: Store<IAppState>,
    protected http: HttpClient,
    private _location: Location,
  ) {}

  // for random place loading sort
  static allIds: string[] = [];
  static loadedIds: string[] = [];

  getPlaces(hubName: string, paramObj: CalendarQueryCommand): Observable<VisitorPlace[]> {
    return this._apiService.get(`visitors/${hubName}/places`, this.toPlaceRequestParams(paramObj)).pipe(
      map((places) => {
        this.processPlaceData(places);
        return places;
      }),
    );
  }

  getPlacesV3(hubName: string, paramObj: CalendarQueryCommand): Observable<VisitorPlace[]> {
    return this._apiService.post(`visitors/${hubName}/places/v3`, this.toPlaceRequestParams(paramObj)).pipe(
      tap((response) => {
        if (response.batchIds && response.batchIds.length > 0) {
          VisitorPlaceService.loadedIds = [...VisitorPlaceService.loadedIds, ...response.batchIds]; // for place random sorting
        }
      }),
      map(
        ({
          total,
          data,
          allIds,
          batchIds,
        }: {
          total: number;
          data: any[];
          allIds: string[];
          batchIds: string[];
        }) => {
          this.processPlaceData(data);
          return data;
          // return {
          //   total,
          //   data,
          //   allIds,
          //   batchIds,
          // };
        },
      ),
    );
  }

  getLocationFilter(hubName: string): Observable<{ locality: string }[]> {
    return this._apiService.get(`visitors/${hubName}/places/location-filter`);
  }

  getGooglePlaceDetails(
    hubName: string,
    placeId: string,
    _id: string,
    waitForUploading = false,
  ): Observable<any> {
    // priceRange is formatted into '$' symbols by the backend response layer.
    return this._apiService.get(
      `visitors/${hubName}/places/google-place-details?placeId=${placeId}&_id=${_id}&waitForUploading=${waitForUploading}`,
    );
  }

  getGooglePlaceReviews(placeId: string, sort: string): Observable<GooglePlaceReview[]> {
    return this._apiService.get(`common/google-reviews?placeId=${placeId}&sort=${sort}`);
  }

  getNearByPlaces(
    targetPlace: VisitorPlace,
    range: number,
    limit: number,
    category?: string,
    categoryList?: string[],
    excludeHotelsForHotelListing = false,
  ): Observable<VisitorPlace[]> {
    return this._apiService
      .post(`visitors/${targetPlace.hubName}/places/nearby-places`, {
        targetPlace,
        range: range * 1609,
        limit,
        category,
        categoryList: isEmptyArray(categoryList) ? null : categoryList,
        ...(excludeHotelsForHotelListing ? { excludeHotelsForHotelListing: true } : {}),
      })
      .pipe(
        switchMap((data) => {
          this.processPlaceData(data);
          return of(data as VisitorPlace[]);
        }),
      );
  }

  getNearByEvents(targetPlace: VisitorPlace, radius: number): Observable<VisitorEvent[]> {
    return this._apiService
      .get(
        `visitors/${targetPlace.hubName}/events/nearby-events?lat=${targetPlace.address.lat}&lng=${targetPlace.address.lng}&radius=${radius}`,
      )
      .pipe(
        map((events) => {
          events.forEach((event) => {
            this._eventTimeService.processEventTimes(event);
          });
          return events;
        }),
      );
  }

  getGoogleImgUrl(photoReference: string, googleAppKey: string): string {
    return ConfigService.config.google.placePhoto
      .replace('${photo_reference}', photoReference)
      .replace('${appKey}', googleAppKey);
  }

  createPlace(hubName, command: CreatePlaceCommand) {
    const categoryCode = command.category
      .split(', ')
      .map((x) => {
        return this.toCategoryCode(x);
      })
      .join(', ');
    command.category = categoryCode;
    return command.isHubCreated
      ? this._apiService.post(`hubs/${hubName}/places`, command)
      : this._apiService.post(`visitors/${hubName}/places`, command);
  }

  public buildFilterPlacesByCustomCategoryCommandFromUrlQuery(
    activatedRoute: ActivatedRoute,
    settings: IFrontendSettings,
    router: any = null,
    allRouteParams: Params = null,
  ): Observable<CalendarQueryCommand> {
    return activatedRoute.queryParams.pipe(
      switchMap((params) => {
        let customPlaceTypeId = '';
        if (window.location.search.includes('ig-custom-places')) {
          const searchParams = new URLSearchParams(window.location.search.split('?')[1]);
          console.log('searchParams for ig-custom-places', searchParams);
          for (const [key, value] of searchParams) {
            if (key === 'ig-custom-places') {
              if (value.includes('-')) {
                customPlaceTypeId = value.split('-').last();
              } else {
                customPlaceTypeId = value;
              }
            }
          }
          console.log(`customEvents in custom place category: ${customPlaceTypeId}`);
        }

        if (params['ig-custom-places']) {
          const value = params['ig-custom-places'];
          if (value.includes('-')) {
            customPlaceTypeId = value.split('-').last();
          } else {
            customPlaceTypeId = value;
          }
        }

        if (!customPlaceTypeId) {
          return of(null);
        }

        const command = new CalendarQueryCommand();

        console.log('settings when buildFilterPlacesByCustomCategoryCommandFromUrlQuery', settings);

        if (settings) {
          let category = this.findCategoryById(
            customPlaceTypeId,
            settings.customSettings.customDefaultPlaceCategory.customCategories,
          );

          if (category) {
            // return to the specified node in URL (only render this node in the dropdown)
            let categoryList = [];
            this.getAllSubCategories(category, categoryList);
            console.log('categoryList load from url', categoryList);
            command.categoryList = categoryList;
            command.categoryNode = category;
            command.category = category.title;
            command.customCategoryId = this.getCustomCategoryId(category);

            if (!params['ig-custom-places']) {
              const currentPath = this._location.path();
              if (currentPath.includes('ig-custom-places=')) {
                this._location.go(
                  currentPath.split('ig-custom-places=')[0] +
                    `ig-custom-places=${this.visitorService.convertToUrlSafeString(
                      category.title,
                    )}-${category._id}`,
                );
              } else {
                const connector = currentPath.includes('?') ? '&' : '?';
                this._location.go(
                  currentPath +
                    `${connector}ig-custom-places=${this.visitorService.convertToUrlSafeString(
                      category.title,
                    )}-${category._id}`,
                );
              }
            }

            if (allRouteParams != null && router != null) {
              if (router.url.includes(`/places/location/group/`)) {
                const groupId = allRouteParams['groupId'];
                const group =
                  settings.customSettings.customPlaceLocationFilterGroups.placeGroups.find(
                    (x) => x._id.toString() === groupId,
                  );
                if (group) {
                  command.locations = group.cities;
                }
              }
              return of(command);
            }
          } else {
            // if the id is not custom category, then try to find it in favorite custom places
            const favCustomPlace = settings.customSettings.customPlaces.find(
              (x) => x._id.toString() === customPlaceTypeId,
            );
            console.log('favCustomPlace load from url', favCustomPlace);
            if (favCustomPlace) {
              this._store.dispatch(
                PlaceQueryCommandActions.setFavoritePlaceBtnId({
                  favBtnId: customPlaceTypeId,
                  hasFavoriteCategoryFromUrl: true,
                }),
              );
            }
          }
        }
        return of(null);
      }),
    );
  }

  /**
   * To get the custom categories (client created custom default categories) of a place
   * E.g., The Olde Pink House, SavannahGA => Where to Eat
   */
  getPlaceCustomCategories(place: VisitorPlace, frontendSettings: IFrontendSettings) {
    if (frontendSettings.customSettings.customDefaultPlaceCategory.useDefault) {
      return null;
    }
    const customCategories =
      frontendSettings.customSettings.customDefaultPlaceCategory.customCategories;

    let customCategoryMatched = [];
    if (customCategories.length) {
      for (const custom of customCategories) {
        let allSubCategories: string[] = [];
        this.getAllSubCategories(custom, allSubCategories);
        if (allSubCategories.some((x) => place.categoryList.includes(this.toReadableCategory(x)))) {
          customCategoryMatched.push(custom);
        }
      }
    }

    return customCategoryMatched;
  }

  getDisplayCategory(place: VisitorPlace): string {
    if (place?.categoryList || place?.category) {
      return (place.categoryList || place.category.split(', '))
        .map((x) => {
          return this.toReadableCategory(x);
        })
        .join(', ')
        .replace(', Point of Interest', '')
        .replace(', Point Of Interest', '')
        .replace(', Establishment', '');
    }

    return '';
  }

  /**
   * tourist_attraction  => Tourist Attraction
   * b&b => B&B
   * arts_&_culture => Arts & Culture
   * */
  toReadableCategory(category: string) {
    if (!category) return '';
    return category
      .trim()
      .replaceAll('&', '_&_')
      .split('_')
      .map((x) => {
        return upperFirstLetter(x);
      })
      .join(' ')
      .replaceAll(' & ', '&');
  }

  /**
   *  Tourist Attraction => tourist_attraction
   * */
  toCategoryCode(category: string) {
    return category.toLowerCase().trim().replaceAll(' ', '_');
  }

  getAllSubCategoriesWithEachLevelTitles(
    item: PlaceType,
    categoryList: string[],
    parentPath: string = '',
  ) {
    const currentPath = parentPath ? `${parentPath} > ${item.title}` : item.title;

    categoryList.push(currentPath);

    // to exclude this situation: "Accommodations > city 1 > City 1"
    // because each level path contains a same name sub-category
    if (
      currentPath.toLowerCase().split(' > ').last() !==
      this.toReadableCategory(item.title).toLowerCase()
    ) {
      categoryList.push(`${currentPath} > ${this.toReadableCategory(item.title)}`);
    }

    // Recurse through subcategories
    if (item.subCategories && item.subCategories.length) {
      for (let subCategory of item.subCategories) {
        this.getAllSubCategoriesWithEachLevelTitles(subCategory, categoryList, currentPath);
      }
    }
  }

  // recursively find category
  findCategoryById(id: string, allCategories: PlaceType[]): PlaceType | null {
    for (const category of allCategories) {
      if (category._id.toString() === id) {
        return category;
      } else {
        for (const iterator of category.subCategories) {
          const result = this.findCategory(id, iterator);
          if (result) return result;
        }
      }
    }
    return null;
  }

  findCategory(id: string, currentNode: PlaceType): PlaceType | null {
    let i, currentChild, result;
    if (id == currentNode._id.toString()) {
      return currentNode;
    } else {
      for (i = 0; i < currentNode.subCategories.length; i += 1) {
        currentChild = currentNode.subCategories[i];
        result = this.findCategory(id, currentChild);
        if (result) {
          return result;
        }
      }
      return null;
    }
  }

  getAllSubCategories(item: PlaceType, categoryList: string[]) {
    if (!item) return;
    if (item.title) {
      categoryList.push(item.title);
      const code = this.toCategoryCode(item.title);
      if (code && code !== item.title) {
        categoryList.push(code);
      }
    }
    if (item.categoryList && item.categoryList.length) {
      categoryList.push(...item.categoryList);
    }
    if (item.subCategories && item.subCategories.length) {
      for (let subCategory of item.subCategories) {
        this.getAllSubCategories(subCategory, categoryList);
      }
    }
  }

  getCustomCategoryId(item: PlaceType | null): string | undefined {
    if (!item) return undefined;
    if (item._id) return String(item._id);
    if (item.id) return String(item.id);
    return undefined;
  }


  private toPlaceRequestParams(paramObj: CalendarQueryCommand): CalendarQueryCommand {
    if (!paramObj) return paramObj;
    const params = { ...paramObj } as CalendarQueryCommand;
    delete params.categoryNode;
    return params;
  }

  checkIsAccommodation(place: VisitorPlace, categories) {
    return categories.any((x) =>
      place.categoryList?.any((y) => x.toLowerCase() === y.toLowerCase()),
    );
  }

  /**
   * Backend API can recognize place categories (ImGoing default categories) in plain text format
   * However frontend URL contains categories in encoded format
   * This function converts encoded categories to plain text before sending to API
   */
  getDefaultCategoryName(category: string): string {
    if (category === 'accommodation') return 'Accommodations';
    if (category === 'dining-nightlife') return 'Dining & Nightlife';
    if (category === 'activities') return 'Activities';
    if (category === 'shopping') return 'Shopping';
    if (category === 'arts-culture') return 'Arts & Culture';
    if (category === 'outdoors-fitness') return 'Outdoors & Fitness';
    if (category === 'entertainment') return 'Entertainment';
    if (category === 'transportation') return 'Travel';
    if (category === 'other') return 'Other';
    if (category === 'sports') return 'Sports';
    if (category === 'health-wellness') return 'Health & Wellness';
    if (category === 'venue-event-services') return 'Venue & Event Services';

    if (category === 'beauty') return 'Beauty';
    if (category === 'cars-motor-services') return 'Cars & Motor Services';
    if (category === 'cleaning-services') return 'Cleaning Services';
    if (category === 'pet-services') return 'Pet Services';
    if (category === 'convenience-pharmacies') return 'Convenience & Pharmacies';
    if (category === 'other-types') return 'Other Types';
    if (category === 'trip-tours-planner') return 'Trip & Tours Planner';
    if (category === 'local-rental-services') return 'Local Rental Services';
    if (category === 'health-personal-care') return 'Health & Personal Care';
    if (category === 'sports-leisure') return 'Sports & Leisure';
    if (category === 'art-culture') return 'Art and Culture';
    if (category === 'food-drink') return 'Food & Drink';
    if (category === 'food-and-drink') return 'Food and Drink';
    if (category === 'nightlife') return 'Nightlife';

    return category;
  }

  getLocationFeeds(hubName: string): Observable<{ title: string; socialName: string }[]> {
    return this._apiService.get(`hubs/${hubName}/location-feeds`);
  }

  getDestinationPlaceFeeds(hubName: string, placeGoogleIds: string[] = []): Observable<any> {
    return this._apiService.get(
      `visitors/${hubName}/places/places-for-travel-buddy${
        placeGoogleIds && placeGoogleIds.length ? `?placeGoogleIds=${placeGoogleIds.join(',')}` : ''
      }`,
    );
  }

  getAllApprovedFeaturedPlaces(hubName: string): Observable<any> {
    return this._apiService.get(`visitors/${hubName}/places/all-featured-places`);
  }

  getPlacesByGoogleIds(hubName: string, placeGoogleIds: string[] = []): Observable<any> {
    return this._apiService.get(
      `visitors/${hubName}/places/by-google-ids${
        placeGoogleIds && placeGoogleIds.length ? `?placeGoogleIds=${placeGoogleIds.join(',')}` : ''
      }`,
    );
  }

  // Youtube video link looks like:
  // https://www.youtube.com/watch?v=puKsjFRwIyw
  // https://www.youtube.com/watch?v=puKsjFRwIyw&t=27s
  getYoutubeLink(place: VisitorPlace): SafeResourceUrl {
    if (place.youtubeLink && place.youtubeLink.includes('watch?v=')) {
      const code = place.youtubeLink.split('watch?v=')[1].split('&')[0];
      const youtubeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        ConfigService.config.youtubeUrl.videoLink.replace('${code}', code),
      );
      return youtubeUrl;
    }
    return null;
  }

  get360VideoLink(place: VisitorPlace): SafeResourceUrl {
    if (place._360VideoKey) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(
        ConfigService.config._360VideoUrl.replace('${id}', place._360VideoKey),
      );
    }
    return null;
  }

  getRatingImg(rating: number) {
    // https://iti-images.s3.amazonaws.com/imgs/rating-5.png
    if (Number.isInteger(rating)) {
      return `https://iti-images.s3.amazonaws.com/imgs/rating-${rating}.png`;
    }

    return `https://iti-images.s3.amazonaws.com/imgs/rating-${Math.floor(rating)}.5.png`;
  }

  searchPlaceByName(
    hubName: string,
    placeName: string,
    cityName: string,
  ): Observable<VisitorPlace | null> {
    return this._apiService.get(
      `visitors/${hubName}/places/search-google-place-by-name?placeName=${placeName}&cityName=${cityName}`,
    );
  }

  getGooglePlaceImageFullUrl(
    reference: string,
    appKey: string,
    isRealReference: boolean,
    isThumb = true,
  ) {
    if (reference === 'null') {
      return '';
    }
    if (reference.startsWith('http')) {
      return reference;
    }
    if (isRealReference) {
      if (isThumb) {
        return config.google.placeRealPhotoThumb.replace('${short_reference}', reference);
      }
      return config.google.placeRealPhoto.replace('${short_reference}', reference);
    }
    if (isThumb) {
      return config.google.placePhotoThumb
        .replace('${photo_reference}', reference)
        .replace('${appKey}', appKey);
    }
    return config.google.placePhoto
      .replace('${photo_reference}', reference)
      .replace('${appKey}', appKey);
  }

  getGooglePlaceReference(googleImgUrl: string) {
    if (googleImgUrl.includes('amazonaws')) return googleImgUrl;

    // https://lh3.googleusercontent.com/p/AF1QipPxB68d-2D5vOFIvPtTIIoWFisgA8vZaIN_uQJB=s1600-w240
    if (googleImgUrl.indexOf('/p/') >= 0 && googleImgUrl.indexOf('=s') >= 0) {
      return googleImgUrl.split('/p/')[1].split('=s')[0];
    }
    // https://maps.googleapis.com/maps/api/place/photo?maxwidth=240&photoreference=ATtYBwIYmydvl-nm5qGNVb6zDxp5pVBvPKYHB5rQsyyk9UZJnfSMYsYS-Iuo4FXhABE1Jx79Rv2o-XD1vwCscdRRT4ZaoZlfbzyCHxEFDB2j_DNmLtia_-PfxZq2zqepiuWn6xRz1T_5nV1jSMXHvRbQT_lmxRbe_UC0O9r1mYfM3zGJ8Sz2&key=AIzaSyAjcpYRF3EQc-LBw5w2_lpzKKeEBDiGLcM
    if (googleImgUrl.indexOf('photoreference=') >= 0 && googleImgUrl.indexOf('&key=') >= 0) {
      return googleImgUrl.split('photoreference=')[1].split('&key=')[0];
    }
    return googleImgUrl;
  }

  private processPlaceData(data) {
    data.forEach((item) => {
      if (!isNullOrUndefined(item.lat) && !isNullOrUndefined(item.lng)) {
        let address = item.address;
        item.address = {
          address,
          lat: item.lat,
          lng: item.lng,
          addressComponents: item.address_components,
        };
      }
      if (item.hours) {
        item.hoursText = [
          'Monday: ',
          'Tuesday: ',
          'Wednesday: ',
          'Thursday: ',
          'Friday: ',
          'Saturday: ',
          'Sunday: ',
        ];
        if (isObject(item.hours)) {
          this.setPlaceHourForObject(item);
        } else if (Array.isArray(item.hours)) {
          item.hours.forEach((x) => {
            this.setPlaceHourText(x.key, item, x);
          });
        }
      }
    });
  }

  private setPlaceHourForObject(item) {
    item.hours.mon_1_open && (item.hoursText[0] += item.hours.mon_1_open);
    item.hours.mon_1_close && (item.hoursText[0] += ` - ${item.hours.mon_1_close}`);
    item.hours.tue_1_open && (item.hoursText[1] += item.hours.tue_1_open);
    item.hours.tue_1_close && (item.hoursText[1] += ` - ${item.hours.tue_1_close}`);
    item.hours.wed_1_open && (item.hoursText[2] += item.hours.wed_1_open);
    item.hours.wed_1_close && (item.hoursText[2] += ` - ${item.hours.wed_1_close}`);
    item.hours.thu_1_open && (item.hoursText[3] += item.hours.thu_1_open);
    item.hours.thu_1_close && (item.hoursText[3] += ` - ${item.hours.thu_1_close}`);
    item.hours.fri_1_open && (item.hoursText[4] += item.hours.fri_1_open);
    item.hours.fri_1_close && (item.hoursText[4] += ` - ${item.hours.fri_1_close}`);
    item.hours.sat_1_open && (item.hoursText[5] += item.hours.sat_1_open);
    item.hours.sat_1_close && (item.hoursText[5] += ` - ${item.hours.sat_1_close}`);
    item.hours.sun_1_open && (item.hoursText[6] += item.hours.sun_1_open);
    item.hours.sun_1_close && (item.hoursText[6] += ` - ${item.hours.sun_1_close}`);
  }

  private setPlaceHourText(key, item, value) {
    switch (key) {
      case 'mon_1_open':
        item.hoursText[0] += value;
        break;
      case 'mon_1_close':
        item.hoursText[0] += ` - ${value}`;
        break;
      case 'tue_1_open':
        item.hoursText[1] += value;
        break;
      case 'tue_1_close':
        item.hoursText[1] += ` - ${value}`;
        break;
      case 'wed_1_open':
        item.hoursText[2] += value;
        break;
      case 'wed_1_close':
        item.hoursText[2] += ` - ${value}`;
        break;
      case 'thu_1_open':
        item.hoursText[3] += value;
        break;
      case 'thu_1_close':
        item.hoursText[3] += ` - ${value}`;
        break;
      case 'fri_1_open':
        item.hoursText[4] += value;
        break;
      case 'fri_1_close':
        item.hoursText[4] += ` - ${value}`;
        break;
      case 'sat_1_open':
        item.hoursText[5] += value;
        break;
      case 'sat_1_close':
        item.hoursText[5] += ` - ${value}`;
        break;
      case 'sun_1_open':
        item.hoursText[6] += value;
        break;
      case 'sun_1_close':
        item.hoursText[6] += ` - ${value}`;
        break;
    }
  }

}
